import { DataTableService } from '../../../../shared/data-table/data-table.service';
import { LazyLoadEvent } from 'primeng/api/primeng-api';
import { PageOptions } from './../../../../shared/data-table/data-table.interface';
import { finalize } from 'rxjs/operators';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ToastrService } from 'ngx-toastr';
import { DataTableValidators } from './../../../../shared/data-table/validators';
import { Component, OnInit } from '@angular/core';
import { CgpConfirmationDialogService } from '../../../../../app/shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { SpanMaintenanceApiService } from '../../../../../modules/api/span-maintenance-api.service';
import { AddSpanModalService } from './add-span/add-span-modal.service';
import { ColumnDef } from '../../../../shared/data-table/data-table.interface';
import { Validators } from '@angular/forms';

@Component({
   templateUrl: './span-maintenance-modal.template.html',
   styleUrls: ['../../tprecm.style.scss', './span-maintenance-modal.scss']
})
export class SpanMaintenanceModalComponent implements OnInit {

   selectedSpan;
   spans: any[];
   loading = false;

   total = 0;
   pageOptions: PageOptions = this.dataTableService.pageOptionsDefaultSort('span');

   /**
    * Column names and values for the span/classes table.
    * Matches the DTO.
    *
    * @type {any[]}
    * @memberof SpanMaintenanceModalComponent
    */
   columns: ColumnDef[] = [
      {
         field: 'span', name: 'Span', width: '8em',
         validators: [
            DataTableValidators.maxLength(50, (message: string) => this.toastr.error(message)),
            DataTableValidators.alphaNumeric((message: string) => this.toastr.error(message))
         ], tooltip: 'Span'
      },
      {
         field: 'conceptWeeks', name: 'Concept Weeks', width: '6em',
         validators: DataTableValidators.numericAndRange((message: string) => this.toastr.error(message)), tooltip: 'Concept Weeks'
      },
      { field: 'designWeeks', name: 'Design Weeks', width: '6em', validators: DataTableValidators.numericAndRange((message: string) => this.toastr.error(message)), tooltip: 'Design Weeks' },
      { field: 'buildWeeks', name: 'Build Weeks', width: '6em', validators: DataTableValidators.numericAndRange( (message: string) => this.toastr.error(message)), tooltip: 'Build Weeks' },
      { field: 'conceptBuffer', name: 'Concept Buffer', width: '6em', validators: DataTableValidators.numericAndRange( (message: string) => this.toastr.error(message)), tooltip: 'Concept Buffer' },
      { field: 'designBuffer', name: 'Design Buffer', width: '6em', validators: DataTableValidators.numericAndRange( (message: string) => this.toastr.error(message)), tooltip: 'Design Buffer' },
      { field: 'buildBuffer', name: 'Build Buffer', width: '6em', validators: DataTableValidators.numericAndRange( (message: string) => this.toastr.error(message)), tooltip: 'Build Buffer' },
      { field: 'weight', name: 'Weight', visible: false, width: '6em', validators: DataTableValidators.numeric(true, (message: string) => this.toastr.error(message)) },
      {
         field: 'examples', name: 'Examples', visible: false,
         validators: DataTableValidators.maxLength(150, (message: string) => this.toastr.error(message))
      },
      {
         field: 'examples1', name: 'Examples of Fixture Types with No Undercut',
         validators: DataTableValidators.maxLength(150, (message: string) => this.toastr.error(message)), tooltip: 'Examples of Fixture Types with No Undercut'
      },
      {
         field: 'examples2', name: 'Examples of Fixture Types with Undercut',
         validators: DataTableValidators.maxLength(150, (message: string) => this.toastr.error(message)), tooltip: 'Examples of Fixture Types with Undercut'
      },
      {
         field: 'examples3', name: 'Examples of Fixture Types with Cheek Rails',
         validators: DataTableValidators.maxLength(150, (message: string) => this.toastr.error(message)), tooltip: 'Examples of Fixture Types with Cheek Rails'
      },
      {
         field: 'mathUnits', name: 'Math Units', width: '6em',
         validators: DataTableValidators.maxLength(10, (message: string) => this.toastr.error(message)), tooltip: 'Math Units'
      },
      { field: 'allocHrs', name: 'Alloc Hrs', visible: false, validators: DataTableValidators.numeric(true, (message: string) => this.toastr.error(message)), tooltip: 'Allocation Hours' },
      { field: 'active', name: 'Active?', type: 'checkbox', width: '6em', tooltip: 'Check box if span is active.' }
   ];

   constructor(
      private readonly cgpConfirmationDialogService: CgpConfirmationDialogService,
      private readonly spanMaintenanceApiService: SpanMaintenanceApiService,
      private readonly addSpanModalService: AddSpanModalService,
      private readonly toastr: ToastrService,
      private readonly dialogRef: MatDialogRef<SpanMaintenanceModalComponent>,
      private readonly dataTableService: DataTableService
   ) { }

   ngOnInit() { }

   onLazyLoad(event: LazyLoadEvent) {
      this.pageOptions.order = event.sortOrder === 1 ? 'asc' : 'desc';
      this.pageOptions.size = event.rows;
      this.pageOptions.sort = event.sortField;
      this.pageOptions.page = event.first / this.pageOptions.size;

      this.loadSpans();
   }

   loadSpans() {
      this.loading = true;
      this.spanMaintenanceApiService.getSpans(this.pageOptions)
         .pipe(finalize(() => this.loading = false))
         .subscribe(response => {
            this.spans = response.data;
            this.total = response.totalCount;
         });
   }

   onAddSpanClick() {
      const dialogRef = this.addSpanModalService.showModal();

      dialogRef.afterClosed()
         .subscribe(() => {
            this.pageOptions.sort = 'id';
            this.pageOptions.order = 'desc';
            this.loadSpans();
         });
   }

   updateSelectedSpan(span) {
      this.selectedSpan = span;
   }

   removeHandler() {
      if (!this.selectedSpan) {
         this.toastr.error('Please select a span to remove.');
         return;
      }

      this.cgpConfirmationDialogService.showConfirmationDialog({
         title: 'TPRECM',
         message: `Are you sure you want to delete span '${this.selectedSpan.span}'?`,
         alert: 'danger',
         onYesClick: () => {
            this.spanMaintenanceApiService.deleteSpan(this.selectedSpan.id)
               .subscribe(response => {
                  this.loadSpans();
                  this.selectedSpan = null;
               });

            this.cgpConfirmationDialogService.closeConfirmationDialog();
         },
         onNoClick: () => this.cgpConfirmationDialogService.closeConfirmationDialog()
      });
   }

   /**
    * Updates the edited span when a change at any row
    * occurs.
    *
    * @memberof SpanMaintenanceModalComponent
    */
   updateSpan(span) {
      const data = span.data;
      data.span = (data.span || '').toUpperCase();

      this.spanMaintenanceApiService.saveSpan(data)
         .subscribe(response =>
            Object.assign(
               this.spans.find(s => s.id === response.id),
               response
            ),
            () => this.loadSpans()
         );
   }

   close() {
      this.dialogRef.close();
   }
}